import { useEffect, useState } from 'react';
import type { MerchantProfilePatch, MerchantRow } from '../../api/types';
import { MERCHANT_DEFAULTS } from '../../utils/merchantDefaults';
import { Modal } from '../ui/Modal';

export type ProfileEditSection = 'legal' | 'risk' | 'technical' | 'settlement';

type EditMerchantProfileModalProps = {
  merchant: MerchantRow | null;
  section: ProfileEditSection;
  open: boolean;
  onClose: () => void;
  onSave: (merchantId: string, body: MerchantProfilePatch) => Promise<void>;
};

const SECTION_TITLES: Record<ProfileEditSection, string> = {
  legal: 'Legal & compliance',
  risk: 'Risk & limits',
  technical: 'Technical',
  settlement: 'Settlement wallet (on-chain)',
};

export function EditMerchantProfileModal({
  merchant,
  section,
  open,
  onClose,
  onSave,
}: EditMerchantProfileModalProps) {
  const [tradeLicenseNumber, setTradeLicenseNumber] = useState('');
  const [licenseExpiryDate, setLicenseExpiryDate] = useState('');
  const [corporateStructure, setCorporateStructure] = useState('');
  const [rollingReserve, setRollingReserve] = useState('5');
  const [dailyLimit, setDailyLimit] = useState('50000');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [solanaWallet, setSolanaWallet] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [staffPassword, setStaffPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!merchant || !open) return;
    setTradeLicenseNumber(merchant.trade_license_number ?? '');
    setLicenseExpiryDate(merchant.license_expiry_date?.slice(0, 10) ?? '');
    setCorporateStructure(merchant.corporate_structure ?? MERCHANT_DEFAULTS.corporate_structure);
    setRollingReserve(String(merchant.rolling_reserve_percent ?? MERCHANT_DEFAULTS.rolling_reserve_percent));
    setDailyLimit(String(merchant.daily_limit_aed ?? MERCHANT_DEFAULTS.daily_limit_aed));
    setWebhookUrl(merchant.webhook_url ?? '');
    setSolanaWallet(merchant.solana_wallet?.startsWith('pending_') ? '' : merchant.solana_wallet ?? '');
    setChangeReason('');
    setStaffPassword('');
    setError(null);
  }, [merchant, open]);

  const submit = async () => {
    if (!merchant) return;

    const patch: MerchantProfilePatch = {};

    if (section === 'legal') {
      patch.trade_license_number = tradeLicenseNumber.trim() || null;
      patch.license_expiry_date = licenseExpiryDate.trim() || null;
      patch.corporate_structure = corporateStructure.trim() || null;
    } else if (section === 'risk') {
      const reserve = Number.parseFloat(rollingReserve);
      const limit = Number.parseFloat(dailyLimit);
      if (!Number.isFinite(reserve) || reserve < 0 || reserve > 100) {
        setError('Rolling reserve must be between 0 and 100');
        return;
      }
      if (!Number.isFinite(limit) || limit < 0) {
        setError('Daily limit must be a positive number');
        return;
      }
      patch.rolling_reserve_percent = reserve;
      patch.daily_limit_aed = limit;
    } else if (section === 'technical') {
      patch.webhook_url = webhookUrl.trim() || null;
    } else {
      const w = solanaWallet.trim();
      const reason = changeReason.trim();
      if (!w || w.length < 32) {
        setError('Enter a valid Solana wallet (base58)');
        return;
      }
      if (reason.length < 10) {
        setError('Compliance reason required (min 10 characters)');
        return;
      }
      if (!staffPassword.trim()) {
        setError('Your staff password is required to confirm this change');
        return;
      }
      patch.solana_wallet = w;
      patch.change_reason = reason;
      patch.staff_password = staffPassword;
    }

    setLoading(true);
    setError(null);
    try {
      await onSave(merchant.id, patch);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Edit — ${SECTION_TITLES[section]}`}
      open={open && Boolean(merchant)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-inline"
            disabled={loading}
            onClick={() => void submit()}
          >
            {loading ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {merchant ? (
        <>
          <p className="modal-muted">{merchant.name}</p>
          {section === 'legal' ? (
            <div className="modal-form-grid">
              <div>
                <label className="login-label" htmlFor="trade-license-number">
                  Trade License Number
                </label>
                <input
                  id="trade-license-number"
                  className="login-input"
                  value={tradeLicenseNumber}
                  onChange={(e) => setTradeLicenseNumber(e.target.value)}
                />
              </div>
              <div>
                <label className="login-label" htmlFor="license-expiry">
                  License Expiry Date
                </label>
                <input
                  id="license-expiry"
                  className="login-input"
                  type="date"
                  value={licenseExpiryDate}
                  onChange={(e) => setLicenseExpiryDate(e.target.value)}
                />
              </div>
              <div className="modal-field-span">
                <label className="login-label" htmlFor="corporate-structure">
                  Corporate Structure
                </label>
                <input
                  id="corporate-structure"
                  className="login-input"
                  value={corporateStructure}
                  onChange={(e) => setCorporateStructure(e.target.value)}
                />
              </div>
            </div>
          ) : null}
          {section === 'risk' ? (
            <div className="modal-form-grid">
              <div>
                <label className="login-label" htmlFor="rolling-reserve">
                  Rolling Reserve %
                </label>
                <input
                  id="rolling-reserve"
                  className="login-input"
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={rollingReserve}
                  onChange={(e) => setRollingReserve(e.target.value)}
                />
              </div>
              <div>
                <label className="login-label" htmlFor="daily-limit">
                  Daily Limit (AED)
                </label>
                <input
                  id="daily-limit"
                  className="login-input"
                  type="number"
                  min={0}
                  step={100}
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(e.target.value)}
                />
              </div>
            </div>
          ) : null}
          {section === 'technical' ? (
            <div>
              <label className="login-label" htmlFor="webhook-url">
                Webhook URL
              </label>
              <input
                id="webhook-url"
                className="login-input"
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://merchant.example/hooks/onyx"
              />
            </div>
          ) : null}
          {section === 'settlement' ? (
            <div>
              <p className="modal-muted" style={{ marginBottom: 12 }}>
                Sets merchant on-chain payout wallet. Changes are audit-logged (staff + DB changelog).
              </p>
              <label className="login-label" htmlFor="solana-wallet">
                Solana owner wallet
              </label>
              <input
                id="solana-wallet"
                className="login-input cell-mono"
                value={solanaWallet}
                onChange={(e) => setSolanaWallet(e.target.value)}
                placeholder="Base58 public key"
              />
              <label className="login-label" htmlFor="change-reason" style={{ marginTop: 12 }}>
                Compliance reason (required)
              </label>
              <textarea
                id="change-reason"
                className="login-input"
                rows={3}
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="Ticket ID, KYB approval reference, or incident number"
              />
              <label className="login-label" htmlFor="staff-password" style={{ marginTop: 12 }}>
                Your staff password (required)
              </label>
              <input
                id="staff-password"
                className="login-input"
                type="password"
                autoComplete="current-password"
                value={staffPassword}
                onChange={(e) => setStaffPassword(e.target.value)}
              />
            </div>
          ) : null}
          {error ? <p className="form-error">{error}</p> : null}
        </>
      ) : null}
    </Modal>
  );
}
